import { toHtmlParagraphs, toTimestamp } from './utils.js';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '');

export async function fetchFeed(limit = 20, offset = 0, viewerId = '') {
  const viewerQuery = viewerId ? `&viewer_id=${encodeURIComponent(viewerId)}` : '';
  const response = await fetch(`${API_BASE}/feed?limit=${limit}&offset=${offset}${viewerQuery}`);
  if (!response.ok) {
    throw new Error(`Feed request failed (${response.status})`);
  }
  const payload = await response.json();
  const items = Array.isArray(payload.items) ? payload.items : [];
  return { items: items.map(normalizePost), hasMore: Boolean(payload.has_more) };
}

export async function fetchSharePayload(postId) {
  const response = await fetch(`${API_BASE}/posts/${encodeURIComponent(postId)}/share-text`);
  if (!response.ok) {
    throw new Error(`Share request failed (${response.status})`);
  }
  return response.json();
}

export async function fetchPost(postId, viewerId = '') {
  const params = viewerId ? `?viewer_id=${encodeURIComponent(viewerId)}` : '';
  const response = await fetch(`${API_BASE}/posts/${encodeURIComponent(postId)}${params}`);
  if (!response.ok) {
    throw new Error(`Post request failed (${response.status})`);
  }
  const item = await response.json();
  return normalizePost(item);
}

export async function fetchLatestAudioEpisode() {
  const response = await fetch(`${API_BASE}/audio/latest`);
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Audio latest request failed (${response.status})`);
  }
  return normalizeAudioEpisode(await response.json());
}

export async function fetchAudioEpisodes(limit = 30) {
  const response = await fetch(`${API_BASE}/audio/episodes?limit=${limit}`);
  if (!response.ok) {
    throw new Error(`Audio episodes request failed (${response.status})`);
  }
  const payload = await response.json();
  const items = Array.isArray(payload) ? payload : [];
  return items.map(normalizeAudioEpisode);
}

export async function subscribeEmail(email) {
  const response = await fetch(`${API_BASE}/subscribers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || `Subscribe failed (${response.status})`);
  }
  return response.json();
}

export async function askStoryRequest(postId, question, viewerId) {
  const response = await fetch(`${API_BASE}/posts/${encodeURIComponent(postId)}/ask`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ question, viewer_id: viewerId })
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || `Ask story failed (${response.status})`);
  }
  return response.json();
}

export async function toggleUpvoteRequest(postId, viewerId) {
  const response = await fetch(`${API_BASE}/posts/${encodeURIComponent(postId)}/upvote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ viewer_id: viewerId })
  });
  if (!response.ok) {
    throw new Error(`Upvote request failed (${response.status})`);
  }
  return response.json();
}

export function normalizePost(item) {
  const comments = Array.isArray(item.comments) ? item.comments : [];
  return {
    id: String(item.id),
    agentId: String(item.agent_id || 'unknown'),
    headline: String(item.title || ''),
    content: toHtmlParagraphs(item.body || ''),
    sourceUrl: item.source_url ? String(item.source_url) : '',
    upvotes: Number(item.upvotes || 0),
    upvotedBy: item.viewer_upvoted ? ['user'] : [],
    postType: String(item.post_type || 'article'),
    topic: item.topic ? String(item.topic) : null,
    comments: comments.map(normalizeComment),
    timestamp: toTimestamp(item.created_at)
  };
}

function normalizeComment(item) {
  return {
    id: String(item.id),
    agentId: String(item.agent_id || 'unknown'),
    content: String(item.body || ''),
    timestamp: toTimestamp(item.created_at)
  };
}

function normalizeAudioEpisode(item) {
  return {
    id: String(item.id || item.date || ''),
    date: String(item.date || ''),
    title: String(item.title || ''),
    summary: String(item.summary || ''),
    durationSeconds: Number(item.duration_seconds || 0),
    storyCount: Number(item.story_count || 0),
    createdAt: toTimestamp(item.created_at),
    audioUrl: String(item.audio_url || ''),
    previewUrl: String(item.preview_url || item.audio_url || ''),
    archiveUrl: String(item.archive_url || '#/briefings')
  };
}
